import { isAxiosError } from 'axios';
import { generateDynamicPath } from 'common/utils';
import { JoinCommunityAsMemberArgs, selectMyCommunitiesIds } from 'community/api';
import { EventStatus } from 'domain/event';
import {
  clearEnteringEvent,
  clearPendingEventEntry,
  selectEnteringEventId,
  selectPendingEventEntry,
} from 'event/entry';
import i18n from 'i18n';
import { addAppConfirmationDialog, addAppErrorMessage, addAppSuccessMessage } from 'modules/alerts/state';
import { getApiErrorMetadata, getErrorMessagesFromResponse } from 'modules/api/utils';
import { AppRoutes } from 'modules/router';
import { selectRouteByPathname } from 'modules/router/state';
import { push } from 'redux-first-history';
import { handleQueryError } from 'store/utils';

import { eventApi } from './eventApi';
import { selectEventCommunityId } from './selectors';
import { DeleteEventArgs, DeleteEventsFromSeriesArgs, RegisterForEventArgs } from './types';

import type { Effect } from 'store';

export const deleteEventSuccessEffect: Effect = ({ meta }, { dispatch, getState }) => {
  const { silentDelete } = meta.arg.originalArgs as DeleteEventArgs;

  const state = getState();
  const route = selectRouteByPathname(state);
  const isEventDetailsPage = route?.path === AppRoutes.EventDetails;

  if (!isEventDetailsPage) return;

  dispatch(push(AppRoutes.MyEvents));

  if (silentDelete) return;

  dispatch(
    addAppConfirmationDialog({
      title: i18n.t('event:deleteEvent.successTitle'),
      description: i18n.t('event:deleteEvent.successDescriptionSingleEvent'),
    })
  );
};

export const deleteEventsFromSeriesSuccessEffect: Effect = ({ meta }, { dispatch }) => {
  const { eventSeries, eventIdsToRemove } = meta.arg.originalArgs as DeleteEventsFromSeriesArgs;

  const eventsToRemove = eventSeries.events.filter(({ id }) => eventIdsToRemove.includes(id));
  const description =
    eventsToRemove.length === 1
      ? i18n.t('event:deleteEvent.successDescriptionEventSeriesSingle', { eventName: eventsToRemove[0].name })
      : i18n.t('event:deleteEvent.successDescriptionEventSeriesMultiple', { count: eventsToRemove.length });

  dispatch(
    addAppConfirmationDialog({
      title: i18n.t('event:deleteEvent.successTitle'),
      description,
    })
  );
};

export const startEventFailedEffect: Effect = ({ payload: error }, { dispatch }) => {
  handleQueryError(error, dispatch, 'eventApi.startMeeting', 'Unable to start meeting');
};

export const startEventSuccessEffect: Effect = ({ meta }, { dispatch }) => {
  const { id } = meta.arg.originalArgs;

  // https://natterco.atlassian.net/browse/N2-1532
  dispatch(
    eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
      data.status = EventStatus.Started;
    })
  );

  dispatch(push(generateDynamicPath(AppRoutes.JoinEvent, { id }), { allow: true }));
};

export const getConnectionsFailedEffect: Effect = ({ payload: error }, { dispatch }) => {
  handleQueryError(error, dispatch, 'eventApi.getConnections', 'Unable to fetch user connections');
};

export const stopEventFailedEffect: Effect = ({ payload: error }, { dispatch }) => {
  if (isAxiosError(error)) {
    getErrorMessagesFromResponse(error.response).forEach((errorMessage) => {
      dispatch(
        addAppErrorMessage({
          metadata: 'MeetingService.stopMeeting',
          message: 'Unable to stop meeting',
          error: new Error(errorMessage),
        })
      );
    });
  }
};

const updateEventDetails: Effect = ({ meta }, { dispatch }) => {
  const { id } = meta.arg.originalArgs;
  dispatch(
    eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
      data.isUserRegistered = true;
    })
  );
};

const addRegisterSuccessMessage: Effect = ({ meta }, { dispatch, getState }) => {
  const state = getState();
  const args = meta.arg.originalArgs as RegisterForEventArgs;

  // Skip success message when silent flag is set (e.g., magic link flow)
  if (args.silent) return;

  const myCommunitiesIds = selectMyCommunitiesIds(state);
  // Prefer communityId from args (for magic link flow), fall back to selector (for event details page flow)
  const communityId = args.communityId ?? selectEventCommunityId(state);

  if (communityId && myCommunitiesIds?.includes(communityId)) {
    dispatch(addAppSuccessMessage('You registered successfully to Event'));
  }
};

// The decoupled register/attend endpoints no longer auto-join the community — membership is now a
// precondition handled by useEnterEvent (join-then-retry). So success only updates the registered
// flag + shows the toast; it does not refresh/join communities.
export const registerForEventSuccessEffect: Effect = (action, listenerApi) => {
  updateEventDetails(action, listenerApi);
  addRegisterSuccessMessage(action, listenerApi);
};

// Live/imminent entry succeeded: flip the registered flag (so the UI offers the Join button).
// No toast — the user is simply entering a live event.
export const attendEventSuccessEffect: Effect = ({ meta }, { dispatch }) => {
  const { id } = meta.arg.originalArgs;

  dispatch(
    eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
      data.isUserRegistered = true;
    })
  );
};

// A NOT_COMMUNITY_MEMBER 403 is expected and handled by useEnterEvent (it joins the community and
// retries), so it must not surface a generic error toast here.
const isNotCommunityMemberError = (error: unknown): boolean =>
  getApiErrorMetadata(error)?.errorType === 'NOT_COMMUNITY_MEMBER';

// A NOT_COMMUNITY_MEMBER 403 is recoverable (join → retry), so the entry intent must survive it. Any
// other failure is terminal: drop the intent for this event so a later, unrelated attendance can't
// auto-navigate the user into the room (see EventJoinButton). register and attend fail identically
// bar the log metadata + toast copy, so they share this factory.
const eventEntryFailedEffect =
  (metadata: string, message: string): Effect =>
  ({ meta, payload: error }, { dispatch, getState }) => {
    const { id } = meta.arg.originalArgs;

    dispatch(
      eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
        data.isUserRegistered = false;
      })
    );
    if (isNotCommunityMemberError(error)) return;
    if (selectEnteringEventId(getState()) === id) dispatch(clearEnteringEvent());
    handleQueryError(error, dispatch, metadata, message);
  };

export const registerForEventFailedEffect = eventEntryFailedEffect(
  'eventApi.registerForEvent',
  'Unable to register to Event'
);

export const attendEventFailedEffect = eventEntryFailedEffect('eventApi.attendEvent', 'Unable to join Event');

// Interactive 403 → join → retry: when the user joins the community (via the MemberSetup dialog in
// join mode) and that join succeeds, immediately retry the parked event entry. Decoupled from the
// component that started the entry and from any cache-refetch timing — the join succeeding is the
// signal. The silent magic-link flow never parks an entry (it retries inline), so this no-ops there.
export const retryEventEntryAfterCommunityJoinEffect: Effect = ({ meta }, { dispatch, getState }) => {
  const pending = selectPendingEventEntry(getState());
  if (!pending) return;

  // Only retry when the community that was just joined is the one this parked entry is waiting on.
  // The listener matches every joinCommunityAsMember success, so without this an abandoned entry
  // (dialog closed without joining) could be fired by an unrelated join — triggering a register/
  // attend (and possibly a confirmation email) the user never confirmed.
  const { communityId: joinedCommunityId } = meta.arg.originalArgs as JoinCommunityAsMemberArgs;
  if (joinedCommunityId !== pending.communityId) return;

  dispatch(clearPendingEventEntry());

  const trigger = pending.mode === 'attend' ? eventApi.endpoints.attendEvent : eventApi.endpoints.registerForEvent;
  void dispatch(trigger.initiate({ id: pending.eventId }));
};

// A genuine community-join failure (network/5xx/validation — not the expected 403 that the entry
// flow handles). Before HNS-5 the join was a side effect of a successful /register, so it couldn't
// fail on its own; now it's an explicit mutation. Always surface it so the failure is never silent.
export const joinCommunityAsMemberFailedEffect: Effect = ({ meta, payload: error }, { dispatch, getState }) => {
  handleQueryError(error, dispatch, 'communityApi.joinCommunityAsMember', 'Unable to join community');

  // Only drop the parked/active entry when the community whose join FAILED is the one that entry is
  // waiting on — mirroring retryEventEntryAfterCommunityJoinEffect's guard. Both an interactive
  // MemberSetup submit and a still-in-flight magic-link silent join dispatch this same action, so an
  // unrelated background join failing must not wipe an entry the user parked for a different community
  // (which would strand their register/attend with no retry — the exact bug HNS-5 targets). Guarding
  // on `pending` also covers `enteringEventId`: the interactive 403 path sets and clears them together,
  // so there is no case where the entering id needs clearing while `pending` is null.
  const { communityId: failedCommunityId } = meta.arg.originalArgs as JoinCommunityAsMemberArgs;
  const pending = selectPendingEventEntry(getState());
  if (pending && failedCommunityId === pending.communityId) {
    dispatch(clearPendingEventEntry());
    dispatch(clearEnteringEvent());
  }
};

export const refreshEventsList: Effect = (_args, { dispatch }) => {
  dispatch(eventApi.util.invalidateTags(['ALL_EVENTS', 'UPCOMING_EVENT', 'EVENT']));
};

/**
 * A WS frame dropped while the socket was down is never redelivered, so a participant waiting on the
 * event details page would never learn the host had started the event (HNS-238). Re-sync the active
 * event on reconnect to close that gap.
 *
 * Invalidating by tag — rather than patching a cache key — reaches the entry whatever id it was fetched
 * under, because `getEventDetails` provides `EVENT` off the numeric `result.id`. Only a query with a
 * live subscriber refetches, so this costs one request for someone actually on the page and nothing
 * for anyone else.
 */
export const resyncActiveEventAfterReconnectEffect: Effect = (_args, { dispatch, getState }) => {
  const { activeEventId } = getState().event;
  if (!activeEventId) return;

  dispatch(eventApi.util.invalidateTags([{ type: 'EVENT', id: activeEventId }]));
};
